import { useState, useEffect, FC } from "react";
import { Card, Button, Typography, Tag, Spin, Badge } from "antd";
import {
  PlusOutlined,
  UpOutlined,
  DownOutlined,
  EyeOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import Title from "antd/es/typography/Title";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import { useDispatch, useSelector } from "react-redux";
import { QueryData } from "../../../../interfaces/IPreAuthQuery";
import { AppDispatch, RootState } from "../../../../store/store";
import { fetchQueries } from "../../../../models/preAuthQuerySlice";
import {
  getQueryPanelStageKey,
  resolveQuerySidebarExpandedStageId,
} from "../../patient-details/newPatientDetailsConstants";
import {
  canActOnRaisedQuery,
  isHospitalPortalUser,
  isOpenQuery,
  isQueryPendingHmoReview,
} from "../../../../utils/queryRespondUtils";
import { applyHmoQueryAction } from "../../../../apis/hmoQueryService";
import { message } from "antd";
import { HMO_ROLE } from "../../../../utils/hmoRoles";
import NmiQueryActionModal, {
  NmiQueryActionType,
} from "../../patient-list/authority/NmiQueryActionModal";
import { PatientListRow } from "../../patient-list/patientListTabConfig";

dayjs.extend(timezone);
const { Text } = Typography;

const resolveQueryStateTagColors = (query: any): { bg: string; text: string; border?: string } => {
  const state = String(query?.stateName || "").toLowerCase();
  const open = isOpenQuery({
    status: query?.status,
    stateName: query?.stateName,
    returnedResponse: query?.returnedResponse,
  });

  if (query?.reQuery && state === "query") {
    return { bg: "#FEE2E2", text: "#B91C1C", border: "1px solid #FECACA" };
  }
  if (state.includes("closed") || state.includes("approved") || state.includes("accepted")) {
    return { bg: "#DCFCE7", text: "#166534", border: "1px solid #86EFAC" };
  }
  if (state.includes("reject") || state.includes("denied")) {
    return { bg: "#FEE2E2", text: "#B91C1C", border: "1px solid #FECACA" };
  }
  if (state.includes("replied") || state.includes("submitted") || state.includes("responded")) {
    return { bg: "#CCFBF1", text: "#0F766E", border: "1px solid #99F6E4" };
  }
  if (open || state === "query" || state.includes("raised")) {
    return { bg: "#FEF3C7", text: "#92400E", border: "1px solid #FCD34D" };
  }
  return { bg: "#E5E7EB", text: "#374151", border: "1px solid #D1D5DB" };
};

type PatientDetailsQueryCardAllStagesProps = {
  activeKey: any;
  handleCreateQuery: (stageId: string) => void;
  visitStages: any[];
  visitId?: number | string;
  caseId?: number | string;
  queryRefreshCounter?: number;
  handleViewQueryModal: (query: QueryData) => void;
  handleReplyQueryModal: (query: QueryData) => void;
  setQueryStageId?: any;
  setActiveKey?: any;
};

const PatientDetailsQueryCardAllStages: FC<
  PatientDetailsQueryCardAllStagesProps
> = ({
  activeKey,
  handleCreateQuery,
  visitStages,
  visitId: visitIdProp,
  caseId: caseIdProp,
  queryRefreshCounter = 0,
  handleViewQueryModal,
  handleReplyQueryModal,
  setQueryStageId,
  setActiveKey,
}) => {
  const { auth, preAuth, patients, queries } = useSelector(
    (state: RootState) => state,
  );
  const dispatch = useDispatch<AppDispatch>();
  const [cardCollapsed, setCardCollapsed] = useState(false);
  //   const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());
  const [stageQueries, setStageQueries] = useState<Record<string, any>>({});
  // const [loadingStages, setLoadingStages] = useState<Record<string, boolean>>(
  //   {},
  // );
  const [loadingStages, setLoadingStages] = useState<boolean>(false);
  const [expandedStageId, setExpandedStageId] = useState<string | null>(null);
  const [stageWisePendingQueryCount, setStageWisePendingQueryCount] = useState<
    Record<string, number>
  >({});
  const [totalPendingQueriesCount, setTotalPendingQueriesCount] =
    useState<number>(0);
  const [queryActionLoadingId, setQueryActionLoadingId] = useState<number | null>(
    null,
  );
  const [queryActionModalOpen, setQueryActionModalOpen] = useState(false);
  const [queryActionType, setQueryActionType] = useState<NmiQueryActionType | null>(
    null,
  );
  const [queryActionTarget, setQueryActionTarget] = useState<QueryData | null>(null);
  const [queryActionLoading, setQueryActionLoading] = useState(false);

  const refreshStageQueries = async () => {
    const caseId =
      caseIdProp ?? patients?.fetchBasicDetails?.casesResponse?.id;
    const visitId =
      visitIdProp ?? patients?.fetchBasicDetails?.visitInfoResponse?.id;

    if (!caseId || !visitId) {
      return;
    }

    setLoadingStages(true);
    try {
      const res: any = await dispatch(
        fetchQueries({
          caseId,
          visitId,
          visitStages,
        }),
      );

      if (res?.meta?.requestStatus === "rejected") {
        console.error("Failed to fetch queries", res?.payload);
        return;
      }

      const responseMap = res?.payload || {};

      const stageWiseQueries: Record<string, any[]> = {};
      const stageWisePending: Record<string, number> = {};
      let totalPending = 0;

      Object.entries(responseMap)?.forEach(([panelKey, queryList]: any) => {
        const pendingCount = (queryList || [])?.filter((q: any) =>
          isOpenQuery(q),
        ).length;

        stageWiseQueries[panelKey] = queryList || [];
        stageWisePending[panelKey] = pendingCount;
        totalPending += pendingCount;
      });

      setStageQueries(stageWiseQueries);
      setStageWisePendingQueryCount(stageWisePending);
      setTotalPendingQueriesCount(totalPending);

      const panelWithQueries = visitStages.find((stage) => {
        const key = getQueryPanelStageKey(stage);
        return (stageWiseQueries[key] || []).length > 0;
      });
      if (panelWithQueries?.visitStageId != null) {
        const expandedPanel = visitStages.find(
          (stage) => String(stage?.visitStageId) === String(expandedStageId),
        );
        const expandedHasQueries =
          (stageWiseQueries[getQueryPanelStageKey(expandedPanel)] || []).length >
          0;
        if (!expandedHasQueries || !expandedStageId) {
          setExpandedStageId(String(panelWithQueries.visitStageId));
        }
      }
    } catch (err) {
      console.error("Failed to fetch queries", err);
    } finally {
      setLoadingStages(false);
    }
  };

  const fetchStageQueries = refreshStageQueries;

  const handleHmoQueryAction = async (
    query: QueryData,
    action: "ACCEPT" | "CLOSE" | "REQUERY",
    remarks?: string,
  ) => {
    const visitId =
      visitIdProp ?? patients?.fetchBasicDetails?.visitInfoResponse?.id;
    if (!visitId || !query?.id) return;
    if (!canActOnRaisedQuery(auth?.role?.role, query.raisedByRole)) {
      message.warning("Only the role that raised this query can approve or re-query it.");
      return;
    }
    setQueryActionLoadingId(query.id);
    try {
      await applyHmoQueryAction(visitId, query.id, action, remarks);
      message.success(
        action === "ACCEPT"
          ? "Query response accepted"
          : action === "CLOSE"
            ? "Query closed"
            : "Re-query sent",
      );
      await refreshStageQueries();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || err?.message || "Unable to apply query action",
      );
    } finally {
      setQueryActionLoadingId(null);
    }
  };

  const openQueryActionModal = (type: NmiQueryActionType, query: QueryData) => {
    setQueryActionType(type);
    setQueryActionTarget(query);
    setQueryActionModalOpen(true);
  };

  const handleQueryActionConfirm = async (payload: {
    type: NmiQueryActionType;
    patient: PatientListRow;
    remarks: string;
  }) => {
    if (!queryActionTarget) return;
    const actionMap: Record<NmiQueryActionType, "ACCEPT" | "CLOSE" | "REQUERY"> = {
      accept: "ACCEPT",
      close: "CLOSE",
      requery: "REQUERY",
    };
    setQueryActionLoading(true);
    try {
      await handleHmoQueryAction(
        queryActionTarget,
        actionMap[payload.type],
        payload.remarks,
      );
      setQueryActionModalOpen(false);
      setQueryActionType(null);
      setQueryActionTarget(null);
    } finally {
      setQueryActionLoading(false);
    }
  };

  //   const toggleStage = (stageId: string) => {
  //     setExpandedStages((prev) => {
  //       const newSet = new Set(prev);
  //       if (newSet.has(stageId)) {
  //         newSet.delete(stageId);
  //       } else {
  //         newSet.add(stageId);
  //         fetchStageQueries(stageId);
  //         // if (!stageQueries[stageId]) {
  //         //   fetchStageQueries(stageId);
  //         // }
  //       }
  //       return newSet;
  //     });
  //   };

  const toggleStage = (stageId: string) => {
    setQueryStageId(stageId);
    setActiveKey(stageId);
    if (expandedStageId === stageId) {
      setExpandedStageId(null);
    } else {
      setExpandedStageId(stageId);
      fetchStageQueries();
    }
  };

  const resolvedCaseId =
    caseIdProp ?? patients?.fetchBasicDetails?.casesResponse?.id;
  const resolvedVisitId =
    visitIdProp ?? patients?.fetchBasicDetails?.visitInfoResponse?.id;

  useEffect(() => {
    if (!resolvedVisitId || !visitStages?.length) {
      return;
    }
    fetchStageQueries();
  }, [resolvedCaseId, resolvedVisitId, visitStages, queryRefreshCounter]);

  useEffect(() => {
    const total = Object.values(stageWisePendingQueryCount)?.reduce(
      (sum: number, count: number) => sum + count,
      0,
    );

    setTotalPendingQueriesCount(total);
  }, [stageWisePendingQueryCount]);

  useEffect(() => {
    const key =
      typeof activeKey === "string" ? activeKey : activeKey?.[0];
    const resolved = resolveQuerySidebarExpandedStageId(key, visitStages);
    if (resolved) {
      setExpandedStageId(resolved);
    }
  }, [activeKey, visitStages]);

  // useEffect(() => {
  //   if (queries?.data?.length > 0) {
  //     setExpandedStageId(queries?.data?.[0]?.stageId?.toString());
  //   }
  // }, [queries?.data]);

  return (
    <Card
      bordered
      style={{
        borderRadius: 12,
        padding: 0,
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
      bodyStyle={{ padding: 0 }}
    >
      <div
        style={{
          backgroundColor: "#004C8F",
          padding: "10px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#fff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text strong style={{ color: "#fff" }}>
            QUERY
          </Text>
          {/* <Tag
            color="white"
            style={{
              fontSize: 11,
              height: 18,
              lineHeight: "16px",
              color: "rgb(0, 84, 157)",
            }}
          >
            {totalPendingQueriesCount || 0}
          </Tag> */}
          {totalPendingQueriesCount > 0 && (
            <div
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Badge
                count={totalPendingQueriesCount || 0}
                overflowCount={99}
                size="small"
                offset={[4, -2]} // controls badge position
                style={{
                  backgroundColor: "#e02424",
                  fontSize: 10,
                }}
              >
                <Tag
                  color="white"
                  style={{
                    fontSize: 11,
                    height: 18,
                    lineHeight: "16px",
                    color: "rgb(0, 84, 157)",
                    margin: 0,
                  }}
                >
                  New
                </Tag>
              </Badge>
            </div>
          )}
        </div>
        <Button
          size="small"
          icon={cardCollapsed ? <DownOutlined /> : <UpOutlined />}
          onClick={() => setCardCollapsed((prev) => !prev)}
        />
      </div>

      {!cardCollapsed && (
        <div
          style={{
            backgroundColor: "#f8f9fa",
            maxHeight: "75vh",
            overflowY: "auto",
          }}
        >
          {visitStages
            ?.filter(
              (stage) => stage?.visitStage?.toUpperCase() !== "RESUBMISSION",
            )
            ?.filter((stage: any) =>
              patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
                "OP" &&
              patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
                "ECHS"
                ? stage?.visitStage?.toUpperCase() !== "PREAUTH"
                : true,
            )
            ?.map((stage) => {
              const stageId = stage?.visitStageId?.toString();
              const panelKey = getQueryPanelStageKey(stage);
              const stageLevelId = stage?.stageLevelId?.toString();
              const stageName = stage?.visitStage;
              // const isExpanded = expandedStages.has(stageId?.toString());
              const isExpanded = expandedStageId === stageId?.toString();
              // const isExpanded = true;

              const queriesData = queries?.data || [];
              //  const isEligible =
              //   ((preAuth?.stepperData?.currentVisitStage?.stageLevelId &&
              //   preAuth?.stepperData?.currentVisitStage?.stageLevelId >=
              //     parseInt(stageLevelId) && auth?.role?.role !== "FO_STAFF") || (stage?.stateId && auth?.role?.role !== "FO_STAFF"));

              const currentStageIndex = visitStages?.findIndex(
                (s) => s.stageLevelId === parseInt(stageLevelId),
              );

              const prevStage =
                currentStageIndex > 0
                  ? visitStages[currentStageIndex - 1]
                  : null;

              // const isEligible =
              //   auth?.role?.role !== "FO_STAFF"
              //     ? (preAuth?.stepperData?.currentVisitStage?.stageLevelId &&
              //         preAuth?.stepperData?.currentVisitStage?.stageLevelId >=
              //           parseInt(stageLevelId) &&
              //         stage?.state?.toLowerCase() !== "approved") ||
              //       (stage?.stateId &&
              //         stage?.state?.toLowerCase() !== "approved") ||
              //       (prevStage &&
              //         [
              //           "submitted",
              //           "manually submitted",
              //           "bill uploaded",
              //           "summary generated",
              //         ].includes(prevStage?.state?.toLowerCase()) &&
              //         stage?.state?.toLowerCase() !== "approved")
              //       ? true
              //       : stage?.state?.toLowerCase() === "approved"
              //       ? false
              //       : false
              //     : false;

              const allowedStates = [
                "submitted",
                "manually submitted",
                "bill uploaded",
                "summary generated",
                "approved",
                "bill added",
                "audited",
              ];

              const stageState = stage?.state?.toLowerCase();
              const currentStageLevelId =
                preAuth?.stepperData?.currentVisitStage?.stageLevelId;

              const isEligible =
                auth?.role?.role !== HMO_ROLE.HMO_PORTAL_USER && auth?.role?.role !== HMO_ROLE.HSP_USER &&
                stageState !== "approved" &&
                ((currentStageLevelId &&
                  currentStageLevelId >= parseInt(stageLevelId)) ||
                  stage?.stateId ||
                  (prevStage &&
                    allowedStates.includes(prevStage?.state?.toLowerCase())));

              const colorCoding = stage?.colorCoding;

              return (
                //   <Card
                //     key={stageId}
                //     style={{ margin: "6px", borderRadius: 8 }}
                //     bodyStyle={{ padding: 12, background: 'rgb(255 249 247)' }}
                //   >
                //     <div
                //       style={{
                //         display: "flex",
                //         justifyContent: "space-between",
                //         alignItems: "center",
                //         cursor: "pointer",
                //         textDecoration: isExpanded ? 'underline solid #008B8B' : "none",
                //         // background: isExpanded ? '#008B8B' : "transparent",
                //         // padding: "8px 4px"
                //       }}
                //       onClick={() => toggleStage(stageId)}
                //     >
                //       <Text strong style={{color: isExpanded ? '#008B8B' : "black", marginLeft: "10px"}}>{stageName}</Text>
                //       <div>
                //         {isEligible && (
                //           <Button
                //             size="small"
                //             icon={<PlusOutlined />}
                //             onClick={(e) => {
                //               e.stopPropagation();
                //               handleCreateQuery(stageId);
                //             }}
                //             style={{ marginRight: 8 }}
                //           />
                //         )}
                //         <Button
                //           size="small"
                //           icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                //         />
                //       </div>
                //     </div>

                //     {isExpanded && (
                //       <div style={{ marginTop: 12 }}>
                //         {loadingStages[stageId] ? (
                //           <Spin />
                //         ) : queriesData?.length ? (
                //           queriesData?.map((query: any) => (
                //             <div
                //               key={query?.id}
                //               style={{
                //                 borderBottom: "0.4px solid #A6A6A6",
                //                 padding: "10px 0",
                //               }}
                //             >
                //               <div
                //                 style={{
                //                   display: "flex",
                //                   alignItems: "center",
                //                   justifyContent: "space-between",
                //                 }}
                //               >
                //                 <div
                //                   style={{
                //                     display: "flex",
                //                     alignItems: "center",
                //                     gap: 8,
                //                   }}
                //                 >
                //                   <Title level={5} style={{ margin: 0, fontSize: "14px" }}>
                //                     {query?.receivedQuery}
                //                   </Title>
                //                   {query?.stateName?.toLowerCase()?.includes("received") && (
                //                     <Tag
                //                       color="red"
                //                       style={{
                //                         fontSize: 10,
                //                         height: 18,
                //                         lineHeight: "16px",
                //                       }}
                //                     >
                //                       new
                //                     </Tag>
                //                   )}
                //                 </div>

                //                 <Tag
                //                   color="#FED7AA"
                //                   style={{
                //                     fontSize: 11,
                //                     height: 18,
                //                     lineHeight: "16px",
                //                     color: "black",
                //                   }}
                //                 >
                //                   {query?.typeOfQuery || "Internal"}
                //                 </Tag>
                //               </div>
                //               <div
                //                 style={{
                //                   display: "flex",
                //                   alignItems: "center",
                //                   justifyContent: "space-between",
                //                 }}
                //               >
                //                 <div>
                //                   <Text
                //                     type="secondary"
                //                     style={{ display: "block", marginTop: 4, fontSize: "11px" }}
                //                   >
                //                     {query?.updatedAt
                //                       ? dayjs(query?.updatedAt)
                //                           .tz("Asia/Kolkata")
                //                           .format("DD MMM YYYY, hh:mm A")
                //                       : null}
                //                   </Text>
                //                   <Tag
                //                     color={
                //                       query?.stateName?.toLowerCase()?.includes("replied")
                //                         ? "#008B8B33"
                //                         : query?.stateName?.toLowerCase()?.includes("received")
                //                         ? "#f0ad4e"
                //                         : "none"
                //                     }
                //                     style={{
                //                       marginTop: 12,
                //                       fontSize: 12,
                //                       borderRadius: 8,
                //                       color:
                //                         query?.stateName?.toLowerCase()?.includes("replied")
                //                           ? "#008B8B"
                //                           : "white",
                //                     }}
                //                   >
                //                     {query?.stateName}
                //                   </Tag>
                //                 </div>
                //                 <div className="icons">
                //                   {query?.stateName?.toLowerCase()?.includes("replied") ? (
                //                     <EyeOutlined
                //                       onClick={() => handleViewQueryModal(query)}
                //                     />
                //                   ) : query?.stateName?.toLowerCase()?.includes("received") ? (
                //                     auth?.role?.role === "FO_STAFF" ? (
                //                       <Button
                //                         icon={<MessageOutlined />}
                //                         style={{
                //                           background: "#00549E",
                //                           color: "white",
                //                           borderRadius: "16px",
                //                         }}
                //                         onClick={() => handleReplyQueryModal(query)}
                //                       >
                //                         Reply
                //                       </Button>
                //                     ) : (
                //                       <EyeOutlined
                //                         onClick={() => handleReplyQueryModal(query)}
                //                       />
                //                     )
                //                   ) : null}
                //                 </div>
                //               </div>
                //             </div>
                //           ))
                //         ) : (
                //           <Text type="secondary">No queries found.</Text>
                //         )}
                //       </div>
                //     )}
                //   </Card>
                <Card
                  key={stageId}
                  style={{ margin: "6px", borderRadius: 8 }}
                  bodyStyle={{
                    padding: 12,
                    background: "rgb(255 249 247)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      padding: "4px 2px",
                      borderRadius: 8,
                      background: colorCoding?.bgHexCode || "rgb(255 249 247)",
                      // textDecoration: isExpanded ? 'underline solid white' : "none",
                    }}
                    onClick={() => toggleStage(stageId)}
                    // onClick={() => setQueryStageId(stageId)}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <Text
                        strong
                        style={{
                          color: colorCoding?.textHexCode || "black",
                          // marginLeft: "10px",
                        }}
                      >
                        {patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
                          "OP" &&
                        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
                          "ECHS" &&
                        stage?.visitStage?.toUpperCase() === "PREAUTH"
                          ? stage?.alternateName
                          : stageName}
                      </Text>

                      {/* <Tag
                        color="rgb(0, 84, 157)"
                        style={{
                          fontSize: 11,
                          height: 18,
                          lineHeight: "16px",
                          color: "white",
                        }}
                      >
                        {stageWisePendingQueryCount?.[stageId] || 0}
                      </Tag> */}
                      {stageWisePendingQueryCount?.[panelKey] > 0 && (
                        <div
                          style={{
                            position: "relative",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Badge
                            count={stageWisePendingQueryCount?.[panelKey] || 0}
                            overflowCount={99}
                            size="small"
                            offset={[4, -2]} // controls badge position
                            style={{
                              backgroundColor: "#e02424",
                              fontSize: 10,
                            }}
                          >
                            <Tag
                              color="rgb(0, 84, 157)"
                              style={{
                                fontSize: 11,
                                height: 18,
                                lineHeight: "16px",
                                color: "white",
                                margin: 0,
                              }}
                            >
                              New
                            </Tag>
                          </Badge>
                        </div>
                      )}
                    </div>
                    <div>
                      {isEligible && (
                        <Button
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateQuery(stageId);
                            setExpandedStageId(null);
                          }}
                          style={{ marginRight: 8 }}
                        />
                      )}
                      <Button
                        size="small"
                        icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                      />
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ marginTop: 12 }}>
                      {loadingStages ? ( //loadingStages[stageId]
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Spin />
                        </div>
                      ) : // queriesData?.length ? (
                      //   queriesData?.map((query: any) => (
                      stageQueries[panelKey]?.length ? (
                        stageQueries[panelKey]?.map((query: any) => {
                          const queryReplied =
                            Boolean(query?.returnedResponse) ||
                            query?.stateName?.toLowerCase()?.includes("replied") ||
                            query?.stateName?.toLowerCase()?.includes("submitted");
                          const queryOpen = isOpenQuery(query);
                          const stateTagColors = resolveQueryStateTagColors(query);

                          return (
                          <div
                            key={query?.id}
                            style={{
                              borderBottom: "0.4px solid #A6A6A6",
                              padding: "10px 0",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <Title
                                  level={5}
                                  style={{ margin: 0, fontSize: "14px" }}
                                >
                                  {query?.updatedRemarks || query?.receivedQuery}
                                </Title>
                                {(queryOpen || query?.stateName === "Query") && !queryReplied && (
                                  // ?.toLowerCase()
                                  // ?.includes("received")
                                  <Tag
                                    color="red"
                                    style={{
                                      fontSize: 10,
                                      height: 18,
                                      lineHeight: "16px",
                                    }}
                                  >
                                    new
                                  </Tag>
                                )}
                              </div>

                              <Tag
                                color="#FED7AA"
                                style={{
                                  fontSize: 11,
                                  height: 18,
                                  lineHeight: "16px",
                                  color: "black",
                                }}
                              >
                                {query?.typeOfQuery || "Internal"}
                              </Tag>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <div>
                                <Text
                                  type="secondary"
                                  style={{
                                    display: "block",
                                    marginTop: 4,
                                    fontSize: "11px",
                                  }}
                                >
                                  {query?.updatedAt
                                    ? dayjs(query?.updatedAt)
                                        .tz("Asia/Kolkata")
                                        .format("DD MMM YYYY, hh:mm A")
                                    : null}
                                </Text>
                                <Tag
                                  color={stateTagColors.bg}
                                  style={{
                                    marginTop: 12,
                                    fontSize: 12,
                                    borderRadius: 8,
                                    color: stateTagColors.text,
                                    border: stateTagColors.border,
                                    fontWeight: 600,
                                  }}
                                >
                                  {query?.reQuery &&
                                  query?.stateName?.toLowerCase() ===
                                    "query" ? (
                                    "Re-Query"
                                  ) : (
                                    <span>{query?.stateName || (queryOpen ? "Query" : "-")}</span>
                                  )}
                                </Tag>
                              </div>
                              <div className="icons">
                                {queryReplied &&
                                !isHospitalPortalUser(auth?.role?.role) &&
                                isQueryPendingHmoReview(query) &&
                                canActOnRaisedQuery(
                                  auth?.role?.role,
                                  query.raisedByRole,
                                ) ? (
                                  <>
                                    <Button
                                      size="small"
                                      loading={queryActionLoadingId === query.id}
                                      style={{
                                        background: "#00549E",
                                        color: "white",
                                        borderRadius: "16px",
                                        marginRight: 8,
                                      }}
                                      onClick={() =>
                                        openQueryActionModal("accept", query)
                                      }
                                    >
                                      Accept
                                    </Button>
                                    <Button
                                      size="small"
                                      loading={queryActionLoadingId === query.id}
                                      style={{
                                        borderRadius: "16px",
                                        marginRight: 8,
                                      }}
                                      onClick={() =>
                                        openQueryActionModal("close", query)
                                      }
                                    >
                                      Close
                                    </Button>
                                    <Button
                                      size="small"
                                      loading={queryActionLoadingId === query.id}
                                      style={{
                                        borderRadius: "16px",
                                        marginRight: 8,
                                      }}
                                      onClick={() =>
                                        openQueryActionModal("requery", query)
                                      }
                                    >
                                      Requery
                                    </Button>
                                    <EyeOutlined
                                      onClick={() => {
                                        handleViewQueryModal(query);
                                      }}
                                    />
                                  </>
                                ) : queryReplied ? (
                                  <EyeOutlined
                                    onClick={() => {
                                      handleViewQueryModal(query);
                                    }}
                                  />
                                ) : queryOpen ? (
                                  (auth?.role?.role === HMO_ROLE.HMO_PORTAL_USER || auth?.role?.role === HMO_ROLE.HSP_USER) ? (
                                    <Button
                                      icon={<MessageOutlined />}
                                      style={{
                                        background: "#00549E",
                                        color: "white",
                                        borderRadius: "16px",
                                      }}
                                      onClick={() => {
                                        handleReplyQueryModal(query);
                                      }}
                                    >
                                      Reply
                                    </Button>
                                  ) : (
                                    <EyeOutlined
                                      onClick={() => {
                                        handleReplyQueryModal(query);
                                      }}
                                    />
                                  )
                                ) : null}
                              </div>
                            </div>
                          </div>
                          );
                        })
                      ) : (
                        <Text type="secondary">No queries found.</Text>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
        </div>
      )}
      <NmiQueryActionModal
        open={queryActionModalOpen}
        type={queryActionType}
        patient={{
          fullName:
            patients?.fetchBasicDetails?.patientResponse?.fullName || "—",
          queryId: queryActionTarget?.id,
        }}
        loading={queryActionLoading}
        onClose={() => {
          setQueryActionModalOpen(false);
          setQueryActionType(null);
          setQueryActionTarget(null);
        }}
        onConfirm={handleQueryActionConfirm}
      />
    </Card>
  );
};

export default PatientDetailsQueryCardAllStages;
